import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  getBusinessUsers,
  getInvitedUsers,
  inviteUser as inviteUserUrl,
  cancelInvitation as cancelInvitationUrl,
  resendInvitation as resendInvitationUrl,
} from 'constants/apiUrls';

// Types for business users
export interface BusinessUser {
  uuid: string;
  user: {
    uuid: string;
    email: string;
    name?: string;
    profilePicture?: string | null;
  };
  role: string;
  isActive: boolean;
  formattedName: string;
  additionalInfo?: string | null;
}

interface BusinessUsersListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: BusinessUser[];
}

// Types for invited users
export interface InvitedUser {
  uuid?: string;
  id?: string;
  invitationId?: string;
  invitation_id?: string;
  email: string;
  status: string;
  createdAt?: string;
  invitedAt?: string;
  created_at?: string;
  invited_at?: string;
}

interface InvitedUsersListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: InvitedUser[];
}

// Request types
interface InviteUserRequest {
  businessUuid: string;
  email: string;
  redirectUrl?: string; // URL where invited user should be redirected after clicking "Join Now"
}

interface InviteUserResponse {
  email: string;
}

interface CancelInvitationRequest {
  businessUuid: string;
  invitationUuid: string;
}

interface ResendInvitationRequest {
  businessUuid: string;
  invitationUuid: string;
}

const businessUsersApi = createApi({
  reducerPath: 'businessUsersApi',
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_SERVER_URL }),
  tagTypes: ['BusinessUsers', 'InvitedUsers'],
  endpoints: (builder) => ({
    // Get business users
    getBusinessUsers: builder.query<
      BusinessUsersListResponse,
      { businessUuid: string; limit?: number; offset?: number }
    >({
      query: ({ businessUuid, limit = 10, offset = 0 }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        return {
          url: getBusinessUsers(businessUuid, limit, offset),
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ['BusinessUsers'],
    }),

    // Get invited users
    getInvitedUsers: builder.query<
      InvitedUsersListResponse,
      { businessUuid: string; limit?: number; offset?: number }
    >({
      query: ({ businessUuid, limit = 10, offset = 0 }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        return {
          url: getInvitedUsers(businessUuid, limit, offset),
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ['InvitedUsers'],
    }),

    // Invite user
    inviteUser: builder.mutation<InviteUserResponse, InviteUserRequest>({
      query: ({ businessUuid, email, redirectUrl }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        // Use the current origin as redirect URL for BWP (Business Web Portal)
        const bwpRedirectUrl = redirectUrl || `${window.location.origin}/sign-up`;
        return {
          url: inviteUserUrl(businessUuid),
          method: 'POST',
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: {
            businessId: businessUuid,
            email,
            redirectUrl: bwpRedirectUrl,
          },
        };
      },
      invalidatesTags: ['InvitedUsers'],
    }),

    // Cancel invitation
    cancelInvitation: builder.mutation<void, CancelInvitationRequest>({
      query: ({ businessUuid, invitationUuid }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        return {
          url: cancelInvitationUrl(businessUuid, invitationUuid),
          method: 'DELETE',
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        };
      },
      invalidatesTags: ['InvitedUsers'],
    }),

    // Resend invitation
    resendInvitation: builder.mutation<InviteUserResponse, ResendInvitationRequest>({
      query: ({ businessUuid, invitationUuid }) => {
        const token = JSON.parse(localStorage.getItem('token') || '""');
        return {
          url: resendInvitationUrl(businessUuid, invitationUuid),
          method: 'POST',
          headers: {
            'bludot-app-name': 'business-rewards',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        };
      },
      invalidatesTags: ['InvitedUsers'],
    }),
  }),
});

export const {
  useGetBusinessUsersQuery,
  useLazyGetBusinessUsersQuery,
  useGetInvitedUsersQuery,
  useLazyGetInvitedUsersQuery,
  useInviteUserMutation,
  useCancelInvitationMutation,
  useResendInvitationMutation,
} = businessUsersApi;

export default businessUsersApi;
